import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, xcss } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { getDateField } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import { EntryCardProps } from './Calendar';

const ContainerDraggingStyles = xcss({
  opacity: 'opacity.disabled',
  backgroundColor: 'elevation.surface.pressed',
});

const StackStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'elevation.surface.raised',
  paddingTop: 'space.100',
  paddingBottom: 'space.100',
  paddingLeft: 'space.150',
  paddingRight: 'space.150',
  transition: '200ms',
  borderRadius: 'border.radius.100',
  boxShadow: 'elevation.shadow.raised',
  ':hover': {
    cursor: 'grab',
    backgroundColor: 'elevation.surface.hovered',
  },
  overflowWrap: 'anywhere',
});

const EntryCard = ({
  entryId,
  EntryCardValueComponent,
}: {
  entryId: string;
  EntryCardValueComponent: React.ComponentType<EntryCardProps>;
}) => {
  const [dragging, setDragging] = useState<boolean>(false);

  const { contentId } = useDatabaseContent();
  const { showPanel } = usePanel();
  const { view } = useView();
  const ref = useRef(null);

  const handleClick = () => {
    showPanel('EntryViewPanel', {
      entryId,
    });
  };

  const [mainFieldId, dateFieldId] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId: contentId as string,
    });
    const shownFields = getDatabaseFields(database);
    const dateField = getDateField(database, view.settings.layout)!;

    return [shownFields[0].id, dateField.id];
  });

  useEffect(() => {
    const el = ref.current;
    invariant(el);

    return draggable({
      element: el,
      getInitialData: () => ({ entryId, fieldId: dateFieldId }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    });
  }, [mainFieldId, entryId, dateFieldId]);

  return (
    <Box
      xcss={[dragging && ContainerDraggingStyles, StackStyles]}
      onClick={handleClick}
      ref={ref}
    >
      <EntryCardValueComponent
        key={mainFieldId}
        contentId={contentId as string}
        entryId={entryId}
        fieldId={mainFieldId}
        isReadonly={true}
      />
    </Box>
  );
};

export default EntryCard;
